import React, { useState, useCallback, useEffect } from 'react';
import { getHighlightedIds, toggleHighlight, clearHighlights } from '../../utils/highlightCache';
import { patchTicket } from '../../api/client';

const ROW_BG = '#1e2e36';
const ROW_ALT = '#1a2a32';
const HEADER_BG = '#263238';
const HIGHLIGHT_BG = '#3e2723'; // amber-dark tint for flagged rows
const HIGHLIGHT_BORDER = '#ff8f00';
const BORDER = '1px solid #37474f';

const S = {
  wrapper: { overflowX: 'auto', fontSize: 13, color: '#eceff1' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  clearBtn: {
    padding: '5px 12px',
    background: '#37474f',
    border: '1px solid #546e7a',
    borderRadius: 5,
    color: '#eceff1',
    cursor: 'pointer',
    fontSize: 12,
  },
  table: { borderCollapse: 'collapse', width: '100%', minWidth: 680 },
  th: {
    padding: '8px 10px',
    background: HEADER_BG,
    borderBottom: '2px solid #455a64',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 12,
    color: '#90caf9',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  thRight: { textAlign: 'right' },
  ticketCell: { padding: '7px 10px', verticalAlign: 'middle', borderBottom: BORDER },
  resourceCell: { padding: '5px 10px 5px 28px', verticalAlign: 'middle', borderBottom: BORDER, color: '#b0bec5' },
  flagCell: { padding: '7px 8px', verticalAlign: 'middle', borderBottom: BORDER, width: 30, cursor: 'pointer' },
  numCell: { padding: '5px 10px', textAlign: 'right', verticalAlign: 'middle', borderBottom: BORDER, fontVariantNumeric: 'tabular-nums' },
  badge: {
    display: 'inline-block',
    padding: '1px 7px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    background: '#1a3a4a',
    color: '#90caf9',
    marginLeft: 6,
  },
  emptyMsg: { padding: '32px 16px', textAlign: 'center', color: '#78909c', fontSize: 14 },
  optionsBtn: {
    padding: '5px 12px',
    background: '#263238',
    border: '1px solid #37474f',
    borderRadius: 5,
    color: '#eceff1',
    cursor: 'pointer',
    fontSize: 12,
  },
  optionsPanel: {
    background: '#1a2a32',
    border: '1px solid #37474f',
    borderRadius: 6,
    padding: '12px 16px',
    marginBottom: 10,
  },
  ruleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  thSortable: {
    cursor: 'pointer',
    userSelect: 'none',
  },
};

function getMatchingColor(summary, rules) {
  if (!summary || !rules || !rules.length) return null;
  const lc = summary.toLowerCase();
  for (const rule of rules) {
    if (rule.contains && lc.includes(rule.contains.toLowerCase())) {
      return rule.color;
    }
  }
  return null;
}

function HoursCell({ value, style = {} }) {
  if (value == null || value === 0) {
    return <td style={{ ...S.numCell, color: '#546e7a', ...style }}>—</td>;
  }
  return <td style={{ ...S.numCell, ...style }}>{Number(value).toFixed(1)}</td>;
}

function formatDate(d) {
  if (!d) return '—';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function TicketHoursView({ ticketRows = [], onAddResource, onAddResourceForTicket, colorRules = [], selectedRuleIds = new Set(), sortCol = null, sortDir = 'asc', onSort, token, showTicketDetails = false, hiddenStatuses = new Set(), onStatusesDiscovered }) {
  const [highlighted, setHighlighted] = useState(() => getHighlightedIds());
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [selectedResources, setSelectedResources] = useState({});
  const [budgetOverrides, setBudgetOverrides] = useState(() => new Map());
  const [editingBudget, setEditingBudget] = useState(null); // { ticketId, value }
  const [savingBudgetId, setSavingBudgetId] = useState(null);
  const [budgetSaveError, setBudgetSaveError] = useState(null);

  const handleToggleHighlight = useCallback((ticketId) => {
    setHighlighted(toggleHighlight(ticketId));
  }, []);

  const handleClearHighlights = useCallback(() => {
    setHighlighted(clearHighlights());
  }, []);

  // Report all statuses present in this week's ticket rows to parent
  useEffect(() => {
    if (!onStatusesDiscovered) return;
    const statuses = new Set(ticketRows.map(r => r.status).filter(Boolean));
    onStatusesDiscovered(statuses);
  }, [ticketRows, onStatusesDiscovered]);

  function toggleExpand(ticketId) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(ticketId) ? next.delete(ticketId) : next.add(ticketId);
      return next;
    });
  }

  function startEditBudget(row) {
    const current = budgetOverrides.has(String(row.ticketId)) ? budgetOverrides.get(String(row.ticketId)) : row.budgetHours;
    setEditingBudget({ ticketId: row.ticketId, value: current != null ? String(current) : '' });
    setBudgetSaveError(null);
  }

  async function saveBudget(ticketId, rawValue) {
    const v = parseFloat(rawValue);
    if (!Number.isFinite(v) || v < 0) { setBudgetSaveError('Enter a valid positive number'); return; }
    setSavingBudgetId(ticketId);
    setBudgetSaveError(null);
    try {
      await patchTicket(ticketId, [{ op: 'replace', path: 'budgetHours', value: v }], token);
      setBudgetOverrides(prev => new Map(prev).set(String(ticketId), v));
      setEditingBudget(null);
    } catch (e) {
      setBudgetSaveError(e?.body || e?.message || 'Failed to save');
    } finally {
      setSavingBudgetId(null);
    }
  }

  function sortIcon(col) {
    if (sortCol !== col) return ' ⇅';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  const SHOW_HIGHLIGHTED = '__highlighted__';
  const highlightCount = highlighted.size;

  // Filter rows by selected options (empty = show all)
  let displayedRows = selectedRuleIds.size === 0
    ? ticketRows
    : ticketRows.filter(row => {
        if (selectedRuleIds.has(SHOW_HIGHLIGHTED) && highlighted.has(String(row.ticketId))) return true;
        const matchedRule = colorRules.find(r => r.contains && row.summary?.toLowerCase().includes(r.contains.toLowerCase()));
        return matchedRule ? selectedRuleIds.has(matchedRule.id) : false;
      });

  // Sort displayed rows
  if (sortCol) {
    displayedRows = [...displayedRows].sort((a, b) => {
      let av, bv;
      if (sortCol === 'ticketNumber') { av = String(a.ticketNumber ?? ''); bv = String(b.ticketNumber ?? ''); return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sortCol === 'project') { av = a.projectName || ''; bv = b.projectName || ''; return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sortCol === 'summary') { av = a.summary || ''; bv = b.summary || ''; return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sortCol === 'budget') { av = a.budgetHours ?? -1; bv = b.budgetHours ?? -1; return sortDir === 'asc' ? av - bv : bv - av; }
      if (sortCol === 'scheduled') { av = a.resources.reduce((s, r) => s + (r.scheduledHours || 0), 0); bv = b.resources.reduce((s, r) => s + (r.scheduledHours || 0), 0); return sortDir === 'asc' ? av - bv : bv - av; }
      if (sortCol === 'completed') { av = a.resources.reduce((s, r) => s + (r.actualHours || 0), 0); bv = b.resources.reduce((s, r) => s + (r.actualHours || 0), 0); return sortDir === 'asc' ? av - bv : bv - av; }
      if (sortCol === 'resource') { av = a.resources[0]?.memberName || a.resources[0]?.memberIdentifier || ''; bv = b.resources[0]?.memberName || b.resources[0]?.memberIdentifier || ''; return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sortCol === 'dateStart') { av = a.dateStart ? new Date(a.dateStart).getTime() : -Infinity; bv = b.dateStart ? new Date(b.dateStart).getTime() : -Infinity; return sortDir === 'asc' ? av - bv : bv - av; }
      if (sortCol === 'dateEnd') { av = a.dateEnd ? new Date(a.dateEnd).getTime() : -Infinity; bv = b.dateEnd ? new Date(b.dateEnd).getTime() : -Infinity; return sortDir === 'asc' ? av - bv : bv - av; }
      if (sortCol === 'status') { av = a.status || ''; bv = b.status || ''; return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
      return 0;
    });
  }

  // Apply hidden-status filter
  if (hiddenStatuses.size > 0) {
    displayedRows = displayedRows.filter(row => !hiddenStatuses.has(row.status || ''));
  }

  if (ticketRows.length === 0) {
    return (
      <div>
        {onAddResource && (
          <div style={{ ...S.toolbar, marginBottom: 12 }}>
            <button
              style={{ ...S.clearBtn, background: '#1565c0', border: '1px solid #0d47a1', color: '#fff' }}
              onClick={onAddResource}
            >
              + Add Resource
            </button>
          </div>
        )}
        <div style={S.emptyMsg}>No resources match these filters.</div>
      </div>
    );
  }

  return (
    <div style={S.wrapper}>
      <div style={S.toolbar}>
        <span style={{ color: '#b0bec5', fontSize: 12 }}>
          {displayedRows.length}{selectedRuleIds.size > 0 && displayedRows.length !== ticketRows.length ? ` of ${ticketRows.length}` : ''} ticket(s) &nbsp;·&nbsp;
          {displayedRows.reduce((s, r) => s + r.resources.length, 0)} assignment(s)
        </span>
        {highlightCount > 0 && (
          <button style={S.clearBtn} onClick={handleClearHighlights}>
            Clear {highlightCount} highlight{highlightCount !== 1 ? 's' : ''}
          </button>
        )}
        {onAddResource && (
          <button
            style={{ ...S.clearBtn, background: '#1565c0', border: '1px solid #0d47a1', color: '#fff' }}
            onClick={onAddResource}
          >
            + Add Resource
          </button>
        )}
      </div>

      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: 30 }} title="Flag for follow-up">★</th>
            <th style={{ ...S.th, ...S.thSortable }} onClick={() => onSort?.('ticketNumber')}>Ticket #{sortIcon('ticketNumber')}</th>
            <th style={{ ...S.th, ...S.thSortable }} onClick={() => onSort?.('project')}>Project{sortIcon('project')}</th>
            <th style={{ ...S.th, ...S.thSortable }} onClick={() => onSort?.('summary')}>Summary{sortIcon('summary')}</th>
            <th style={{ ...S.th, ...S.thSortable }} onClick={() => onSort?.('resource')}>Resource{sortIcon('resource')}</th>
            {showTicketDetails && <th style={{ ...S.th, ...S.thSortable }} onClick={() => onSort?.('status')}>Status{sortIcon('status')}</th>}
            {showTicketDetails && <th style={{ ...S.th, ...S.thSortable }} onClick={() => onSort?.('dateStart')}>Start{sortIcon('dateStart')}</th>}
            {showTicketDetails && <th style={{ ...S.th, ...S.thSortable }} onClick={() => onSort?.('dateEnd')}>End{sortIcon('dateEnd')}</th>}
            <th style={{ ...S.th, ...S.thRight, ...S.thSortable }} onClick={() => onSort?.('budget')}>Budget{sortIcon('budget')}</th>
            <th style={{ ...S.th, ...S.thRight, ...S.thSortable }} onClick={() => onSort?.('scheduled')}>Scheduled{sortIcon('scheduled')}</th>
            <th style={{ ...S.th, ...S.thRight, ...S.thSortable }} onClick={() => onSort?.('completed')}>Completed{sortIcon('completed')}</th>
            <th style={{ ...S.th, width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {displayedRows.map((row, idx) => {
            const isHighlighted = highlighted.has(String(row.ticketId));
            const isExpanded = expandedIds.has(row.ticketId);
            const matchedColor = getMatchingColor(row.summary, colorRules);
            const rowBg = matchedColor
              ? matchedColor + '33'
              : isHighlighted ? HIGHLIGHT_BG : idx % 2 === 0 ? ROW_BG : ROW_ALT;
            const borderLeft = matchedColor
              ? `3px solid ${matchedColor}`
              : isHighlighted ? `3px solid ${HIGHLIGHT_BORDER}` : '3px solid transparent';

            // Aggregate hours for the ticket summary row
            const totalSched = row.resources.reduce((s, r) => s + (r.scheduledHours || 0), 0);
            const totalActual = row.resources.reduce((s, r) => s + (r.actualHours || 0), 0);

            return (
              <React.Fragment key={row.ticketId}>
                {/* Ticket summary row */}
                <tr style={{ background: rowBg, borderLeft }}>
                  <td
                    style={{ ...S.flagCell, color: isHighlighted ? HIGHLIGHT_BORDER : '#546e7a' }}
                    onClick={() => handleToggleHighlight(String(row.ticketId))}
                    title={isHighlighted ? 'Remove flag' : 'Flag for follow-up'}
                  >
                    {isHighlighted ? '★' : '☆'}
                  </td>
                  <td style={{ ...S.ticketCell, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => toggleExpand(row.ticketId)}
                      style={{ background: 'none', border: 'none', color: '#90caf9', cursor: 'pointer', fontSize: 12, padding: '0 4px 0 0' }}
                      title={isExpanded ? 'Collapse' : 'Expand resources'}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </button>
                    {row.ticketNumber}
                  </td>
                  <td style={S.ticketCell}>{row.projectName || row.projectId || '—'}</td>
                  <td style={{ ...S.ticketCell, fontWeight: 600 }}>{row.summary}</td>
                  <td style={S.ticketCell}>
                    {row.resources.length === 1 && (
                      <span style={{ color: '#b0bec5', fontSize: 12 }}>
                        {row.resources[0].memberName || row.resources[0].memberIdentifier || '(unknown)'}
                      </span>
                    )}
                    {row.resources.length > 1 && (
                      <select
                        value={selectedResources[row.ticketId] ?? 0}
                        onChange={e => setSelectedResources(prev => ({ ...prev, [row.ticketId]: Number(e.target.value) }))}
                        style={{ background: '#263238', color: '#b0bec5', border: '1px solid #37474f', borderRadius: 4, fontSize: 12, padding: '1px 4px' }}
                      >
                        {row.resources.map((res, ri) => (
                          <option key={ri} value={ri}>
                            {res.memberName || res.memberIdentifier || '(unknown)'}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  {showTicketDetails && <td style={{ ...S.ticketCell, whiteSpace: 'nowrap', fontSize: 12, color: '#90caf9' }}>{row.status || '—'}</td>}
                  {showTicketDetails && <td style={{ ...S.ticketCell, whiteSpace: 'nowrap', fontSize: 12, color: '#b0bec5' }}>{formatDate(row.dateStart)}</td>}
                  {showTicketDetails && <td style={{ ...S.ticketCell, whiteSpace: 'nowrap', fontSize: 12, color: '#b0bec5' }}>{formatDate(row.dateEnd)}</td>}
                  {editingBudget?.ticketId === row.ticketId ? (
                    <td style={{ ...S.numCell, padding: '4px 6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' }}>
                        <input
                          type="number" min="0" step="0.5"
                          value={editingBudget.value}
                          onChange={e => setEditingBudget(prev => ({ ...prev, value: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') saveBudget(row.ticketId, editingBudget.value); if (e.key === 'Escape') setEditingBudget(null); }}
                          autoFocus
                          style={{ width: 64, background: '#263238', border: '1px solid #546e7a', borderRadius: 4, color: '#eceff1', padding: '2px 5px', fontSize: 12, textAlign: 'right' }}
                        />
                        <button onClick={() => saveBudget(row.ticketId, editingBudget.value)} disabled={savingBudgetId === row.ticketId} style={{ padding: '2px 5px', background: '#1565c0', border: 'none', borderRadius: 3, color: '#fff', cursor: 'pointer', fontSize: 11 }} title="Save">{savingBudgetId === row.ticketId ? '…' : '✓'}</button>
                        <button onClick={() => { setEditingBudget(null); setBudgetSaveError(null); }} style={{ padding: '2px 5px', background: 'none', border: '1px solid #546e7a', borderRadius: 3, color: '#b0bec5', cursor: 'pointer', fontSize: 11 }} title="Cancel">✕</button>
                      </div>
                      {budgetSaveError && <div style={{ color: '#ef5350', fontSize: 10, marginTop: 2, textAlign: 'right' }}>{budgetSaveError}</div>}
                    </td>
                  ) : (
                    <td
                      className={onAddResourceForTicket ? 'rm-budget-editable' : undefined}
                      style={{ ...S.numCell, fontWeight: 600, cursor: onAddResourceForTicket ? 'pointer' : 'default' }}
                      onClick={onAddResourceForTicket ? () => startEditBudget(row) : undefined}
                      title={onAddResourceForTicket ? 'Click to edit budget hours' : undefined}
                    >
                      {(() => {
                        const v = budgetOverrides.has(String(row.ticketId)) ? budgetOverrides.get(String(row.ticketId)) : row.budgetHours;
                        const display = (v == null || v === 0) ? <span style={{ color: '#546e7a' }}>—</span> : Number(v).toFixed(1);
                        if (!onAddResourceForTicket) return display;
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, borderBottom: '1px dashed #546e7a', paddingBottom: 1 }}>
                            {display}
                            <span style={{ fontSize: 9, color: '#546e7a', lineHeight: 1 }}>✎</span>
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <HoursCell value={totalSched > 0 ? totalSched : null} style={{ fontWeight: 600 }} />
                  <HoursCell value={totalActual > 0 ? totalActual : null} style={{ fontWeight: 600 }} />
                  <td style={{ ...S.ticketCell, textAlign: 'right', padding: '4px 8px' }}>
                    {onAddResourceForTicket && (
                      <button
                        style={{ padding: '3px 8px', background: '#1565c0', border: '1px solid #0d47a1', borderRadius: 4, color: '#fff', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}
                        onClick={() => onAddResourceForTicket(row)}
                      >
                        + Add
                      </button>
                    )}
                  </td>
                </tr>

                {/* Resource sub-rows (shown when expanded) */}
                {isExpanded && row.resources.map((res, ri) => {
                  const isDone = res.actualHours > 0 && !res.scheduledHours;
                  return (
                    <tr
                      key={`${row.ticketId}-${res.memberIdentifier || ri}`}
                      style={{ background: matchedColor ? matchedColor + '22' : isHighlighted ? '#3a1f18' : '#1a2a32' }}
                    >
                      <td style={S.flagCell} />
                      <td style={S.resourceCell} />
                      <td style={S.resourceCell} />
                      <td style={{ ...S.resourceCell, color: '#cfd8dc' }}>
                        ↳ {res.memberName || res.memberIdentifier || '(unknown)'}
                      </td>
                      <td style={S.resourceCell} />
                      {showTicketDetails && <td style={S.resourceCell} />}
                      {showTicketDetails && <td style={S.resourceCell} />}
                      {showTicketDetails && <td style={S.resourceCell} />}
                      <td style={S.numCell} />
                      <HoursCell value={res.scheduledHours} />
                      {isDone ? (
                        <td style={{ ...S.numCell, textAlign: 'right' }}>
                          <span style={{ color: '#66bb6a', fontWeight: 600 }}>Completed</span>
                          <span style={{ color: '#78909c', fontSize: 11, marginLeft: 5 }}>({Number(res.actualHours).toFixed(1)}h)</span>
                        </td>
                      ) : (
                        <HoursCell value={res.actualHours} />
                      )}
                      <td style={S.numCell} />
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
